import React, { useContext } from 'react'
import { Container, Row, Col, Card } from 'react-bootstrap'
import { Title } from 'components'
import { I18nContext } from 'i18n'
import styled from 'styled-components'
import { useLandscapeReports } from 'hooks/useLandscapeReports'
import ContentLoader from 'react-content-loader'
import { FaPrint } from 'react-icons/fa'
import Markdown from 'react-markdown'

const LandscapeReportsPage = () => {
  const { translate } = useContext(I18nContext)
  const { data, loading, error, downloadPdf, getPdfUrl } = useLandscapeReports()
  
  const handleImageClick = (reportId, fileName) => {
    downloadPdf(reportId, fileName)
  }

  const handlePrint = (reportId) => {
    const pdfUrl = getPdfUrl(reportId)
    window.open(pdfUrl, '_blank')
  }
  
  if (loading) {
    return (
      <Container>
        <Row>
          <Col>
            <Title>{translate('landscape_reports_title')}</Title>
          </Col>
        </Row>
        <Row>
          <Col>
            <ContentLoader
              speed={2}
              width={400}
              height={150}
              viewBox="0 0 400 150"
              backgroundColor="#f3f3f3"
              foregroundColor="#ecebeb"
            >
              <rect x="0" y="0" rx="3" ry="3" width="300" height="20" />
              <rect x="0" y="30" rx="3" ry="3" width="400" height="15" />
              <rect x="0" y="55" rx="3" ry="3" width="400" height="15" />
              <rect x="0" y="80" rx="3" ry="3" width="350" height="15" />
              <rect x="0" y="110" rx="3" ry="3" width="200" height="30" />
            </ContentLoader>
          </Col>
        </Row>
      </Container>
    )
  }

  if (error) {
    return (
      <Container>
        <Row>
          <Col>
            <Title>{translate('landscape_reports_title')}</Title>
          </Col>
        </Row>
        <Row>
          <Col>
            <p style={{ color: 'red', fontStyle: 'italic' }}>Error loading reports</p>
          </Col>
        </Row>
      </Container>
    )
  }
  
  return (
    <Container>
      <Row>
        <Col>
          <Title>{translate('landscape_reports_title')}</Title>
        </Col>
      </Row>
      <Row>
        <Col>
          <Subtitle>{translate('landscape_reports_overview')}</Subtitle>
          <p className='pl-4'>{translate('landscape_reports_overview_description')}</p>
        </Col>
      </Row>
      {data?.length > 0 && (
        <Row className="mb-4">
          <Col>
            <ReportList>
              {data.map((report) => (
                <li key={report.id}>
                  <ReportLink href={`#report-${report.id}`}>{report.name}</ReportLink>
                </li>
              ))}
            </ReportList>
          </Col>
        </Row>
      )}
      {data?.length > 0 ? (
        data?.map((report) => (
          <Row key={report.id} className="mb-4" id={`report-${report.id}`}>
            <Col>
              <ReportCard>
                <Card.Body>
                  <Subtitle>{report.name}</Subtitle>
                  <Markdown className='pl-4'>{report.description}</Markdown>
                  <Row className="mb-2">
                    <Col className="d-flex justify-content-end">
                      <PrintButton onClick={() => handleImageClick(report.id, report.fileName)}>
                        <FaPrint size="1.2rem" style={{ marginRight: '0.5rem' }} />
                        {translate('print_report')}
                      </PrintButton>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col>
                      <PreviewImage
                        src={report.previewImage}
                        alt={report.name}
                        onClick={() => handlePrint(report.id)}
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </ReportCard>
            </Col>
          </Row>
        ))
      ) : (
        <Row>
          <Col>
            <p style={{ fontStyle: 'italic' }}>{translate('no_reports_available')}</p>
          </Col>
        </Row>
      )}
    </Container>
  )
}

const Subtitle = styled.legend`
  color: ${(props) => props.theme.header.title};
  font-size: 1.2rem;
  white-space: nowrap;
  font-weight: 600;
`

const ReportCard = styled(Card)`
  border: px solid #DFDFDF;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`

const PreviewImage = styled.img`
  width: 100%;
  height: auto;
  cursor: pointer;
  border: 1px solid #ddd;
  border-radius: 4px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`

const PrintButton = styled.button`
  border: none;
  background-color: ${({ theme }) => theme.palette.grayscale[8]};
  color: ${({ theme }) => theme.palette.primary[0]};
`;

const ReportList = styled.ul`
  padding-left: 2.5rem;
  margin-bottom: 1rem;
`

const ReportLink = styled.a`
  color: ${({ theme }) => theme.palette.primary[0]};
  text-decoration: none;
  font-size: 1rem;
  display: block;
  padding: 0.5rem 0;
  transition: color 0.2s ease;
  
  &:hover {
    color: ${({ theme }) => theme.palette.primary[1]};
    text-decoration: underline;
  }
`

export default LandscapeReportsPage
